import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

// Sprint 38 — Contractual Precision NavItem.
// Active state: 3px left-edge accent bar in --primary, bold weight,
// no rounded corners. Hover: 10% ink overlay.

interface NavItemProps {
  label: string;
  url: string;
  icon: LucideIcon;
  active: boolean;
  counter?: number;
  /** Render compact (icon-only) for tablet collapsed rail. */
  collapsed?: boolean;
  /** Optional click handler (e.g. for closing mobile drawer on nav). */
  onClick?: () => void;
  /** Indentation level for nested items inside a section. */
  level?: number;
}

export function NavItem({
  label,
  url,
  icon: Icon,
  active,
  counter,
  collapsed,
  onClick,
  level = 0,
}: NavItemProps) {
  return (
    <Link
      to={url}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-label={label}
      className={cn(
        'group relative flex items-center gap-3 px-4 py-2 font-public-sans text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'bg-primary/10 dark:bg-primary/20 font-bold text-sidebar-active dark:text-sidebar-accent'
          : 'font-semibold text-sidebar-foreground hover:bg-primary/[0.06] dark:hover:bg-primary/[0.12] hover:text-sidebar-active dark:hover:text-foreground',
        level > 0 && 'pl-8',
        collapsed && 'justify-center px-2',
      )}
    >
      {/* 3px left accent bar on active — teal in light, sky-teal in dark. */}
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-y-0 left-0 w-[3px] rounded-r-sm',
          active ? 'bg-sidebar-active dark:bg-sidebar-accent' : 'bg-transparent',
        )}
      />
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {!collapsed && (
        <>
          <span className="truncate flex-1 uppercase tracking-[0.04em]">{label}</span>
          {typeof counter === 'number' && counter > 0 && (
            <span
              className={cn(
                'ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-primary/15 text-primary dark:bg-primary/25',
              )}
            >
              {counter}
            </span>
          )}
        </>
      )}
    </Link>
  );
}
